import { createDraftSafeSelector, createEntityAdapter, createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';
import { ActionsObservable, StateObservable, combineEpics } from 'redux-observable';
import { of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, delay, filter, mergeMap } from 'rxjs/operators';

import { extractPaginationValues, getPaginationQueryParams } from '~features/page/pageUtils';
import { http } from '~services/http';
import { saveFile } from '~utils/page';
import { getResponseError } from '~utils/response';

import { actions as notificationsActions } from '../notifications/notificationsSlice';
import { actions as pageActions } from '../page/pageSlice';
import { actions as sharedActions } from '../sharedSlice';

import { appRoutes, api } from '~constants';
import { Customer, PaginationRequestPayload, PaginationResponse, ResponseError } from '~models';

const customerEntity = createEntityAdapter<Customer>({
  selectId: customer => customer.id,
});

type CustomersState = {
    data: ReturnType<typeof customerEntity.getInitialState>;
    loading: {
      list: boolean;
      details: boolean;
      create: boolean;
      import: boolean;
      export: boolean;
      update: boolean;
      delete: boolean;
    };
    error: ResponseError | null;
    status: 'updated' | 'imported' | null;
};

export const initialState: CustomersState = {
  data: customerEntity.getInitialState(),
  loading: {
    list: false,
    details: false,
    create: false,
    import: false,
    export: false,
    update: false,
    delete: false,
  },
  error: null,
  status: null,
};

export const { name, reducer, actions } = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    // fetch customers
    fetchCustomersInit(state, payload: PayloadAction<PaginationRequestPayload>) {
      state.loading.list = true;
    },

    fetchCustomersSuccess(state, { payload: { content } }: PayloadAction<PaginationResponse<Customer>>) {
      state.loading.list = false;
      state.data = customerEntity.setAll(state.data, content);
    },

    fetchCustomersFailed(state) {
      state.loading.list = false;
    },

    // fetch customer by ID
    fetchCustomerByIdInit(state, { payload: { id } }: PayloadAction<{ id: Customer['id'] }>) {
      state.loading.details = true;
    },

    fetchCustomerByIdSuccess(state, { payload }: PayloadAction<Customer>) {
      state.loading.details = false;
      customerEntity.upsertOne(state.data, payload);
    },

    fetchCustomerByIdFailed(state) {
      state.loading.details = false;
    },

    // fetch customers ids
    fetchCustomersIdsInit(state) {
      state.loading.list = true;
    },

    fetchCustomersIdsSuccess(state, { payload }: PayloadAction<Customer[]>) {
      state.loading.list = false;
      customerEntity.setAll(state.data, payload);
    },

    // create customer
    createCustomerInit(state, action: PayloadAction<Partial<Customer>>) {
      state.loading.create = true;
      state.error = null;
    },

    createCustomerSuccess(state) {
      state.loading.create = false;
    },

    createCustomerFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.create = false;
      state.error = payload;
    },

    // delete customer
    deleteCustomerInit(state, { payload }: PayloadAction<{ id: Customer['id'] }>) {
      state.loading.delete = true;
    },

    deleteCustomerSuccess(state) {
      state.loading.delete = false;
    },

    deleteCustomerFailed(state) {
      state.loading.delete = false;
    },

    // update customer
    updateCustomerInit(state, { payload }: PayloadAction<{ id: Customer['id'], values: Customer }>) {
      state.loading.update = true;
      state.status = null;
      state.error = null;
    },

    updateCustomerSuccess(state) {
      state.loading.update = false;
      state.status = 'updated';
    },

    updateCustomerFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.update = false;
      state.error = payload;
    },

    // import customer CSV file
    importCSVFilesInit(state, { payload: { files } }: PayloadAction<{ files: File[] }>) {
      state.loading.import = true;
      state.status = null;
    },

    importCSVFilesSuccess(state) {
      state.loading.import = false;
      state.status = 'imported';
    },

    importCSVFilesFailed(state) {
      state.loading.import = false;
    },

    exportCustomerCSVTemplateInit(state) {
      state.loading.export = true;
    },

    exportCustomerCSVTemplateSuccess(state) {
      state.loading.export = false;
    },

    exportCustomerCSVTemplateFailed(state) {
      state.loading.export = false;
    },

    resetCustomersData(state) {
      Object.assign(state, initialState);
    },

    resetCustomerStatus(state) {
      state.status = null;
    },
  },

  extraReducers: {
    [sharedActions.reset.toString()]: state => {
      Object.assign(state, initialState);
    },
  },
});

const getCustomersState = (state: AES.RootState) => state.customers;
const customersSelectors = customerEntity.getSelectors();

export const selectors = {
  getCustomersState,

  getCustomersList: createDraftSafeSelector(getCustomersState, state => customersSelectors.selectAll(state.data)),
  getCustomerById: (id: Customer['id']) =>
    createDraftSafeSelector(getCustomersState, state => customersSelectors.selectById(state.data, id)),
  getCustomerLoaders: createDraftSafeSelector(getCustomersState, state => state.loading),
  getCustomerError: createDraftSafeSelector(getCustomersState, state => state.error),
  isCustomerUpdated: createDraftSafeSelector(getCustomersState, state => state.status === 'updated'),
  isCustomerImported: createDraftSafeSelector(getCustomersState, state => state.status === 'imported'),
};

const fetchCustomersEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchCustomersInit.match),
    mergeMap(({ payload }) => {
      const searchParams = getPaginationQueryParams(payload);
      return http.getJSON<PaginationResponse<Customer>>(api.customers.list(searchParams)).pipe(
        mergeMap(res =>
          of(
            actions.fetchCustomersSuccess(res),
            pageActions.setPagePagination(extractPaginationValues(res)))
        ),
        catchError(() => of(actions.fetchCustomersFailed()))
      );
    })
  );

const fetchCustomerByIdEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchCustomerByIdInit.match),
    mergeMap(({ payload: { id } }) =>
      http.getJSON<Customer>(api.customers.byId(id)).pipe(
        mergeMap(res => of(actions.fetchCustomerByIdSuccess(res))),
        catchError(() => of(actions.fetchCustomerByIdFailed())),
      )
    )
  );

const createCustomerEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.createCustomerInit.match),
    mergeMap(({ payload }) =>
      http.post(api.customers.create, payload).pipe(
        mergeMap(() => of(
          actions.createCustomerSuccess(),
          notificationsActions.enqueue({
            message: 'Customer added',
            options: { variant: 'success' },
          }),
          push(appRoutes.customers),
        )),
        catchError(err => of(
          actions.createCustomerFailed(getResponseError(err))
        ))
      )
    )
  );

const deleteCustomerEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.deleteCustomerInit.match),
    mergeMap(({ payload: { id } }) =>
      http.delete(api.customers.byId(id)).pipe(
        mergeMap(() =>
          of(
            actions.deleteCustomerSuccess(),
            notificationsActions.enqueue({
              message: 'Customer deleted',
              options: { variant: 'success' },
            }),
            push(appRoutes.customers)
          )),
        catchError(() => of(actions.deleteCustomerFailed()))
      )
    )
  );

const updateCustomerEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateCustomerInit.match),
    mergeMap(({ payload: { id, values } }) =>
      http.put(api.customers.byId(id), values).pipe(
        mergeMap(() => of(
          actions.updateCustomerSuccess(),
          notificationsActions.enqueue({
            message: 'Customer updated',
            options: { variant: 'success' },
          })
        )),
        catchError(err => of(actions.updateCustomerFailed(getResponseError(err))))
      ))
  );

const fetchCustomersIdsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchCustomersIdsInit.match),
    mergeMap(() =>
      http.getJSON<Customer[]>(api.customers.ids).pipe(
        mergeMap(res => of(actions.fetchCustomersIdsSuccess(res))),
        catchError(() => of(actions.fetchCustomersFailed()))
      )
    )
  );

const importCSVFilesEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<AES.RootState>) =>
  action$.pipe(
    filter(actions.importCSVFilesInit.match),
    mergeMap(({ payload: { files } }) => {
      const formData = new FormData();

      files.forEach(file => formData.append('file', file, file.name));
      return ajax({
        method: 'POST',
        url: api.customers.import,
        body: formData,
        headers: {
          Authorization: `Bearer ${state$.value.auth.accessToken}`,
        },
      }).pipe(
        delay(300),
        mergeMap(() =>
          of(
            actions.importCSVFilesSuccess(),
            notificationsActions.enqueue({
              message: 'Customer CSV File Uploaded',
              options: { variant: 'success' },
            }),
            push(appRoutes.customers),
          )
        ),
        catchError(err => of(
          actions.importCSVFilesFailed(),
          notificationsActions.enqueue({
            message: getResponseError(err),
            options: { variant: 'error' },
          })
        ))
      );
    })
  );

const exportCustomerCSVTemplateEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.exportCustomerCSVTemplateInit.match),
    mergeMap(() =>
      http.call({
        method: 'GET',
        url: api.customers.importTemplate,
        responseType: 'blob' as 'json',
      })
        .pipe(
          mergeMap(res => {
            saveFile(res.response, 'customer_template.csv');

            return of(
              actions.exportCustomerCSVTemplateSuccess(),
              notificationsActions.enqueue({
                message: 'Customer CSV Template has been downloaded',
                options: { variant: 'success' },
              })
            );
          }),
          catchError(() => of(actions.exportCustomerCSVTemplateFailed()))
        )
    )
  );

export const epics = combineEpics(
  fetchCustomersEpic,
  createCustomerEpic,
  fetchCustomerByIdEpic,
  deleteCustomerEpic,
  updateCustomerEpic,
  fetchCustomersIdsEpic,
  importCSVFilesEpic,
  exportCustomerCSVTemplateEpic,
);

export const allEpics = {
  fetchCustomersEpic,
  createCustomerEpic,
  fetchCustomerByIdEpic,
  deleteCustomerEpic,
  updateCustomerEpic,
  fetchCustomersIdsEpic,
  importCSVFilesEpic,
  exportCustomerCSVTemplateEpic,
};

declare global {
    namespace AES {
      export interface Actions {
        customers: typeof actions;
      }

      export interface RootState {
        customers: CustomersState;
      }

      export interface Selectors {
        customers: typeof selectors;
      }
    }
  }
